// Rykkere for ubetalte fakturaer efter renteloven (§ 9b) og inkassoloven (§ 10).
// Ren beregning i øre (heltal), så skærmen, testversionen og databasen (20261028840000_rykkere.sql) regner ens.
//   • Betalingspåmindelse (gratis) → Rykker 1 → Rykker 2 → Rykker 3/inkassovarsel (10 dages frist) → klar til inkasso.
//   • Højst 3 rykkergebyrer à højst 100 kr. (momsfri), og mindst 10 dage mellem rykkere med gebyr.
//   • Rente fra forfaldsdagen: referencesats + 8 % p.a., simpel rente pr. dag.
//   • Erhvervskunder (CVR på kunden): kompensation 310 kr. én gang (fra første rykker med gebyr).
// Alle dage, beløb og satser kan rettes pr. virksomhed – men aldrig over lovens grænser.
import { plusDage } from './medlemskab.ts';
import { kr } from './pris.ts';
import { datoTekst } from './dato.ts';
import type { Skabelon } from './automation.ts';

/** Et trin, der sendes: dag efter forfald og gebyr (øre, momsfrit). */
export type RykkerTrinRegel = { dag: number; gebyr: number };
export type RykkerKanal = 'mail' | 'sms' | 'begge';
/** Hvor en sag går hen efter inkassovarslet: Collectia (inkassobureau) eller småsag ved domstolene (minretssag.dk). */
export type InkassoSpor = 'collectia' | 'smaasag';
export type RykkerRegler = {
  betalingsfrist: number;          // dage fra fakturadato (bookingens dato) til forfald
  trin: RykkerTrinRegel[];         // [påmindelse, rykker 1, rykker 2, rykker 3/inkassovarsel]
  inkassoFrist: number;            // dage i inkassovarslet, før sagen kan sendes til inkasso
  maxGebyrer: number;              // højst så mange rykkergebyrer
  minDageMellem: number;           // mindst så mange dage mellem rykkere med gebyr
  rente: number;                   // % p.a. (referencesats + 8)
  kompensation: number;            // øre, erhvervskunder, én gang
  kanal: RykkerKanal;
  inkassoPrivat: InkassoSpor;      // forslag for private kunder
  smaasagFra: number;              // øre: erhvervskrav fra dette beløb (hovedstol) foreslås som småsag – ellers Collectia
  collectiaKreditor: string;       // kreditornummer hos Collectia (kommer med i eksporten)
};

/** Lovens grænser (renteloven § 9b og inkassoloven § 10). */
export const LOV = { gebyr: 10000, maxGebyrer: 3, minDageMellem: 10, inkassoFrist: 10, kompensation: 31000, maxRente: 30, smaasagMax: 5000000 };

export const STANDARD_RYKKER: RykkerRegler = {
  betalingsfrist: 8,
  trin: [{ dag: 3, gebyr: 0 }, { dag: 10, gebyr: 10000 }, { dag: 20, gebyr: 10000 }, { dag: 30, gebyr: 10000 }],
  inkassoFrist: 10, maxGebyrer: 3, minDageMellem: 10, rente: 10.6, kompensation: 31000, kanal: 'begge',
  inkassoPrivat: 'collectia', smaasagFra: 500000, collectiaKreditor: '',
};

/** Trinene i forløbet (tragten): 0 faktura sendt … 6 inkasso. Niveau 1–4 = de fire beskeder, der sendes. */
export const TRIN = [
  { navn: 'Faktura sendt', kort: 'Faktura sendt' },
  { navn: 'Forfalden', kort: 'Forfalden' },
  { navn: 'Betalingspåmindelse', kort: 'Påmindelse' },
  { navn: 'Rykker 1', kort: 'Rykker 1' },
  { navn: 'Rykker 2', kort: 'Rykker 2' },
  { navn: 'Rykker 3 / inkassovarsel', kort: 'Inkassovarsel' },
  { navn: 'Inkasso', kort: 'Inkasso' },
] as const;
/** Navnet på beskeden for et niveau (1 = påmindelse … 4 = inkassovarsel). */
export const niveauNavn = (n: number) => TRIN[n + 1]?.kort ?? '';

export type RykkerHaendelse =
  | { type: 'sendt'; niveau: number; dato: string; tid: string; af: string; kanaler: ('mail' | 'sms')[]; gebyr: number; kompensation: number; rente: number }
  | { type: 'pause'; dato: string; tid: string; af: string; til: string; grund: string }
  | { type: 'inkasso'; dato: string; tid: string; af: string; spor?: InkassoSpor; sagsnr?: string }
  | { type: 'sagsnr'; dato: string; tid: string; af: string; sagsnr: string }
  | { type: 'genoptag' | 'betalt'; dato: string; tid: string; af: string };

/** Rykkerstatus for én booking (tabellen rykkere). */
export type RykkerStatus = {
  niveau: number;                  // 0 = intet sendt, 1 = påmindelse … 4 = inkassovarsel
  gebyrer: number;                 // rykkergebyrer pålagt i alt (øre)
  kompensation: number;            // kompensation pålagt (øre)
  pause_til: string | null; pause_grund: string;
  inkasso: string | null;          // dato, sagen blev sendt til inkasso (Collectia) eller forberedt som småsag
  inkasso_spor: InkassoSpor | null;
  inkasso_sagsnr: string;          // Collectias eller rettens sagsnummer ('' = ikke tilføjet endnu)
  inkasso_fil: string | null;      // småsagens PDF-pakke i det private lager
  betalt: string | null;           // dato, bookingen blev betalt (efter at rykkerforløbet var startet)
  historik: RykkerHaendelse[];
};
export const TOM_STATUS: RykkerStatus = { niveau: 0, gebyrer: 0, kompensation: 0, pause_til: null, pause_grund: '', inkasso: null, inkasso_spor: null, inkasso_sagsnr: '', inkasso_fil: null, betalt: null, historik: [] };

/** Linjer på bookingens gebyrer, som rykkerforløbet lægger på (momsfri). */
export const RYKKER_LINJER = ['rykker', 'kompensation', 'rente'] as const;

const dag = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / 864e5;
/** Dage fra a til b (b − a). */
export const dageMellem = (a: string, b: string) => Math.round(dag(b) - dag(a));

/** Simpel rente pr. dag i øre. Satsen regnes i hundrededele procent, så resultatet er det samme som i databasen. */
export function rente(hovedstol: number, satsProcent: number, dage: number): number {
  if (hovedstol <= 0 || dage <= 0) return 0;
  return Math.round((hovedstol * Math.round(satsProcent * 100) * dage) / 3650000);
}

const heltal = (v: unknown, min: number, max: number, std: number) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : std; };

/** Kreditornummer hos Collectia: bogstaver, tal og bindestreg (tomt = ikke angivet). */
const KREDITOR = /^[A-Za-z0-9-]{0,30}$/;

/** Gemte regler lagt oven på standarden og holdt inden for lovens grænser. */
export function normRegler(g: unknown): RykkerRegler {
  const r = (g && typeof g === 'object' ? g : {}) as Partial<RykkerRegler>;
  const s = STANDARD_RYKKER;
  const trin = s.trin.map((t, i) => ({ dag: heltal(r.trin?.[i]?.dag, 0, 365, t.dag), gebyr: heltal(r.trin?.[i]?.gebyr, 0, LOV.gebyr, t.gebyr) }));
  const sats = Number(r.rente);
  return {
    betalingsfrist: heltal(r.betalingsfrist, 0, 90, s.betalingsfrist), trin,
    inkassoFrist: heltal(r.inkassoFrist, LOV.inkassoFrist, 90, s.inkassoFrist),
    maxGebyrer: heltal(r.maxGebyrer, 0, LOV.maxGebyrer, s.maxGebyrer),
    minDageMellem: heltal(r.minDageMellem, LOV.minDageMellem, 90, s.minDageMellem),
    rente: Number.isFinite(sats) ? Math.min(LOV.maxRente, Math.max(0, Math.round(sats * 100) / 100)) : s.rente,
    kompensation: heltal(r.kompensation, 0, LOV.kompensation, s.kompensation),
    kanal: r.kanal === 'mail' || r.kanal === 'sms' || r.kanal === 'begge' ? r.kanal : s.kanal,
    inkassoPrivat: r.inkassoPrivat === 'smaasag' || r.inkassoPrivat === 'collectia' ? r.inkassoPrivat : s.inkassoPrivat,
    smaasagFra: heltal(r.smaasagFra, 0, LOV.smaasagMax, s.smaasagFra),
    collectiaKreditor: typeof r.collectiaKreditor === 'string' && KREDITOR.test(r.collectiaKreditor.trim()) ? r.collectiaKreditor.trim() : s.collectiaKreditor,
  };
}

/** Tjek regler, før de gemmes. Fejl pr. felt (tom = ok). */
export function tjekRegler(r: RykkerRegler): Record<string, string> {
  const f: Record<string, string> = {};
  const ok = (v: number, min: number, max: number) => Number.isInteger(v) && v >= min && v <= max;
  if (!ok(r.betalingsfrist, 0, 90)) f.betalingsfrist = 'Mellem 0 og 90 dage.';
  if (r.trin.length !== 4) f.trin = 'Der skal være fire trin.';
  r.trin.forEach((t, i) => {
    if (!ok(t.dag, 1, 365)) f[`dag${i}`] = 'Mellem 1 og 365 dage efter forfald.';
    else if (i > 0 && t.dag <= r.trin[i - 1].dag) f[`dag${i}`] = 'Skal ligge efter det forrige trin.';
    if (!ok(t.gebyr, 0, LOV.gebyr)) f[`gebyr${i}`] = 'Højst 100 kr. pr. rykker (renteloven).';
  });
  r.trin.forEach((t, i) => { if (i > 0 && t.gebyr > 0 && r.trin[i - 1].gebyr > 0 && t.dag - r.trin[i - 1].dag < r.minDageMellem && !f[`dag${i}`]) f[`dag${i}`] = `Mindst ${r.minDageMellem} dage efter forrige rykker med gebyr.`; });
  if (!ok(r.inkassoFrist, LOV.inkassoFrist, 90)) f.inkassoFrist = 'Mindst 10 dage (inkassoloven).';
  if (!ok(r.maxGebyrer, 0, LOV.maxGebyrer)) f.maxGebyrer = 'Højst 3 rykkergebyrer (renteloven).';
  if (!ok(r.minDageMellem, LOV.minDageMellem, 90)) f.minDageMellem = 'Mindst 10 dage (renteloven).';
  if (!(Number.isFinite(r.rente) && r.rente >= 0 && r.rente <= LOV.maxRente)) f.rente = 'Mellem 0 og 30 %.';
  if (!ok(r.kompensation, 0, LOV.kompensation)) f.kompensation = 'Højst 310 kr. (renteloven).';
  if (!['mail', 'sms', 'begge'].includes(r.kanal)) f.kanal = 'Vælg mail, SMS eller begge.';
  if (!['collectia', 'smaasag'].includes(r.inkassoPrivat)) f.inkassoPrivat = 'Vælg Collectia eller småsag.';
  if (!ok(r.smaasagFra, 0, LOV.smaasagMax)) f.smaasagFra = 'Mellem 0 og 50.000 kr. (småsagsgrænsen).';
  if (!KREDITOR.test(r.collectiaKreditor)) f.collectiaKreditor = 'Kun bogstaver, tal og bindestreg (højst 30 tegn).';
  return f;
}

export type RykkerInput = {
  dato: string;                    // fakturadato (bookingens dato)
  hovedstol: number;               // fakturaens beløb inkl. moms, uden rykkerlinjer (øre)
  erhverv: boolean;                // kunden har CVR
  status: RykkerStatus | null;
};

/** Det, der sker, hvis næste besked sendes på en bestemt dag. */
export type RykkerSend = { niveau: number; gebyr: number; kompensation: number; rente: number; grund: string | null };

export type RykkerNaeste =
  | { type: 'send'; niveau: number; dato: string; klar: boolean }          // næste besked og hvornår den bør sendes
  | { type: 'pause'; til: string }
  | { type: 'inkasso_frist'; dato: string }                                // inkassovarslet er sendt – fristen løber
  | { type: 'inkasso_klar'; dato: string }                                 // fristen er udløbet – kontoret sender selv til inkasso
  | { type: 'hos_inkasso'; dato: string }
  | { type: 'betalt'; dato: string };

export type RykkerBeregning = {
  forfald: string; dageOver: number; trin: number; niveau: number;
  gebyrer: number; antalGebyrer: number; kompensation: number;
  renteDage: number; renter: number; iAlt: number;
  naeste: RykkerNaeste;
  /** Sendes næste besked i dag: niveau, gebyr, kompensation og renter (null = intet kan sendes). */
  nu: RykkerSend | null;
};

const sendte = (s: RykkerStatus) => s.historik.filter((h): h is Extract<RykkerHaendelse, { type: 'sendt' }> => h.type === 'sendt');

/** Hvad giver det at sende næste besked på dagen `d`? Gebyret er 0, hvis loven ikke tillader det den dag. */
export function sendPaa(i: RykkerInput, r: RykkerRegler, d: string): RykkerSend | null {
  const s = i.status ?? TOM_STATUS;
  if (s.niveau >= 4 || s.inkasso || s.betalt) return null;
  const n = s.niveau + 1;
  const forfald = plusDage(i.dato, r.betalingsfrist);
  const medGebyr = sendte(s).filter((x) => x.gebyr > 0);
  const sidst = medGebyr.at(-1)?.dato ?? null;
  const fuld = r.trin[n - 1]?.gebyr ?? 0;
  let grund: string | null = null;
  if (fuld > 0) {
    if (d <= forfald) grund = 'Fakturaen er ikke forfalden endnu – rykkeren sendes uden gebyr.';
    else if (medGebyr.length >= r.maxGebyrer) grund = `Der er allerede pålagt ${medGebyr.length} rykkergebyrer (højst ${r.maxGebyrer}).`;
    else if (sidst && dageMellem(sidst, d) < r.minDageMellem) grund = `Der skal gå mindst ${r.minDageMellem} dage mellem rykkere med gebyr – rykkeren sendes uden gebyr.`;
  }
  const gebyr = fuld > 0 && !grund ? fuld : 0;
  const kompensation = i.erhverv && s.kompensation === 0 && n >= 2 && d > forfald ? r.kompensation : 0;
  return { niveau: n, gebyr, kompensation, rente: rente(i.hovedstol, r.rente, Math.max(0, dageMellem(forfald, d))), grund };
}

/** Hele beregningen for én faktura på dagen `idag`. */
export function beregnRykker(i: RykkerInput, r: RykkerRegler, idag: string): RykkerBeregning {
  const s = i.status ?? TOM_STATUS;
  const forfald = plusDage(i.dato, r.betalingsfrist);
  const dageOver = dageMellem(forfald, idag);
  const slut = s.betalt && s.betalt < idag ? s.betalt : idag;
  const renteDage = Math.max(0, dageMellem(forfald, slut));
  const renter = rente(i.hovedstol, r.rente, renteDage);
  const ss = sendte(s);
  const trin = s.inkasso ? 6 : s.niveau >= 1 ? Math.min(5, s.niveau + 1) : dageOver >= 0 ? 1 : 0;
  let naeste: RykkerNaeste;
  if (s.betalt) naeste = { type: 'betalt', dato: s.betalt };
  else if (s.inkasso) naeste = { type: 'hos_inkasso', dato: s.inkasso };
  else if (s.pause_til && s.pause_til >= idag) naeste = { type: 'pause', til: s.pause_til };
  else if (s.niveau >= 4) {
    const varsel = ss.filter((x) => x.niveau === 4).at(-1)?.dato ?? idag;
    const dato = plusDage(varsel, r.inkassoFrist);
    naeste = idag >= dato ? { type: 'inkasso_klar', dato } : { type: 'inkasso_frist', dato };
  } else {
    const n = s.niveau + 1;
    let dato = plusDage(forfald, r.trin[n - 1].dag);
    const sidst = ss.filter((x) => x.gebyr > 0).at(-1)?.dato;
    if (r.trin[n - 1].gebyr > 0 && sidst && ss.filter((x) => x.gebyr > 0).length < r.maxGebyrer) {
      const tidligst = plusDage(sidst, r.minDageMellem);
      if (tidligst > dato) dato = tidligst;
    }
    naeste = { type: 'send', niveau: n, dato, klar: idag >= dato };
  }
  const pause = naeste.type === 'pause';
  return {
    forfald, dageOver, trin, niveau: s.niveau,
    gebyrer: s.gebyrer, antalGebyrer: ss.filter((x) => x.gebyr > 0).length, kompensation: s.kompensation,
    renteDage, renter, iAlt: i.hovedstol + s.gebyrer + s.kompensation + renter, naeste,
    nu: pause ? null : sendPaa(i, r, idag),
  };
}

/** Er fakturaen klar til næste besked (forfalden efter planen og ikke på pause)? */
export const erForfalden = (b: RykkerBeregning) => b.naeste.type === 'send' && b.naeste.klar;

/** Nøgletal og trin-tragt for alle rykkersager (beløb i øre). trin[i] = sager på trin i (0–6). Samme tal som rykker_liste i databasen. */
export type RykkerOpsummering = {
  antal: number; iAlt: number; forfaldne: number; forfaldneBeloeb: number; aeldste: number;
  gebyrer: number; antalGebyrer: number; kompensationer: number; renter: number; tilSend: number;
  trin: { antal: number; beloeb: number }[];
};

/** Kan næste besked sendes nu ("Send alle forfaldne"): forfalden efter planen, ikke før forfald og kunden kan nås på en kanal. */
export const kanSendesNu = (b: RykkerBeregning, kanaler: readonly string[]) => erForfalden(b) && !!b.nu && b.dageOver >= 0 && kanaler.length > 0;

export function opsummerRykkere(l: { b: RykkerBeregning; kanaler: readonly string[] }[]): RykkerOpsummering {
  const o: RykkerOpsummering = { antal: l.length, iAlt: 0, forfaldne: 0, forfaldneBeloeb: 0, aeldste: 0, gebyrer: 0, antalGebyrer: 0, kompensationer: 0, renter: 0, tilSend: 0,
    trin: TRIN.map(() => ({ antal: 0, beloeb: 0 })) };
  for (const { b, kanaler } of l) {
    o.iAlt += b.iAlt; o.gebyrer += b.gebyrer + b.kompensation; o.antalGebyrer += b.antalGebyrer; o.renter += b.renter;
    if (b.kompensation > 0) o.kompensationer++;
    if (b.dageOver > 0) { o.forfaldne++; o.forfaldneBeloeb += b.iAlt; o.aeldste = Math.max(o.aeldste, b.dageOver); }
    if (kanSendesNu(b, kanaler)) o.tilSend++;
    o.trin[b.trin].antal++; o.trin[b.trin].beloeb += b.iAlt;
  }
  return o;
}

/** Bookingens beløb uden rykkerlinjer = fakturaens hovedstol. */
export function hovedstolAf(b: { total: number; gebyrer?: { type: string; beloeb: number; moms: number }[] | null }): number {
  return b.total - (b.gebyrer ?? []).filter((g) => (RYKKER_LINJER as readonly string[]).includes(g.type)).reduce((s, g) => s + g.beloeb + g.moms, 0);
}

/** Kunden er erhverv, når der står et CVR-nummer på kunden. */
export const erErhverv = (felter: Record<string, string> | null | undefined) => /\d{8}/.test((felter?.cvr ?? '').replace(/\D/g, ''));

/** Kanaler, beskeden sendes på: reglens kanal, men kun dem, kunden har (e-mail/mobil). */
export function kanalerFor(k: RykkerKanal, kontakt: { email: string; telefon: string }): ('mail' | 'sms')[] {
  return ([['mail', !!kontakt.email.trim()], ['sms', !!kontakt.telefon.trim()]] as const).filter(([x, har]) => har && (k === 'begge' || k === x)).map(([x]) => x);
}

// ---------- Beskeder ----------
export const RYKKER_SKABELON_ID = ['rykker_paamindelse', 'rykker_1', 'rykker_2', 'rykker_3'] as const;
const FOD = '\n\nHar du allerede betalt, kan du se bort fra denne besked. Spørgsmål? Ring på {{telefon}}.\n\nVenlig hilsen\n{{firma}}';
/** Standardteksterne. Kan rettes under Kommunikation › Beskeder (samme id). */
export const RYKKER_SKABELONER: Skabelon[] = [
  { id: 'rykker_paamindelse', navn: 'Rykker: Betalingspåmindelse', emne: 'Venlig påmindelse om betaling – booking {{booking_nr}}',
    mail: 'Hej {{kunde_fornavn}}\n\nVi kan se, at vi endnu ikke har modtaget betaling for din booking {{booking_nr}}, som forfaldt {{forfald}}. Det er sikkert bare en forglemmelse.\n\n{{opgoerelse}}\n\nDu kan betale med MobilePay eller kort her:\n{{link_betal}}\n\nDenne påmindelse er gratis.' + FOD,
    sms: 'Hej {{kunde_fornavn}}. Vi mangler betaling for booking {{booking_nr}} ({{i_alt}}), som forfaldt {{forfald}}. Betal her: {{link_betal}} Mvh {{firma}}' },
  { id: 'rykker_1', navn: 'Rykker: Rykker 1', emne: 'Rykker: manglende betaling for booking {{booking_nr}}',
    mail: 'Hej {{kunde_fornavn}}\n\nVi har stadig ikke modtaget betaling for din booking {{booking_nr}}, som forfaldt {{forfald}}. Efter renteloven har vi derfor lagt et rykkergebyr på {{gebyr}} på, og der løber renter på {{rente_sats}} om året fra forfaldsdagen.\n\n{{opgoerelse}}\n\nBetal senest {{frist}} her:\n{{link_betal}}' + FOD,
    sms: 'Hej {{kunde_fornavn}}. Rykker: booking {{booking_nr}} er ikke betalt. Der er lagt gebyr og renter på – i alt {{i_alt}}. Betal senest {{frist}}: {{link_betal}} Mvh {{firma}}' },
  { id: 'rykker_2', navn: 'Rykker: Rykker 2', emne: '2. rykker: booking {{booking_nr}} er stadig ikke betalt',
    mail: 'Hej {{kunde_fornavn}}\n\nTrods vores tidligere rykker har vi ikke modtaget betaling for din booking {{booking_nr}}, som forfaldt {{forfald}}. Vi har lagt endnu et rykkergebyr på {{gebyr}} på, og renterne løber fortsat.\n\n{{opgoerelse}}\n\nBetal senest {{frist}} her, så undgår du flere omkostninger:\n{{link_betal}}' + FOD,
    sms: 'Hej {{kunde_fornavn}}. 2. rykker: booking {{booking_nr}} mangler stadig betaling – nu {{i_alt}}. Betal senest {{frist}}: {{link_betal}} Mvh {{firma}}' },
  { id: 'rykker_3', navn: 'Rykker: Rykker 3 / inkassovarsel', emne: 'Inkassovarsel: booking {{booking_nr}}',
    mail: 'Hej {{kunde_fornavn}}\n\nVi har fortsat ikke modtaget betaling for din booking {{booking_nr}}, som forfaldt {{forfald}}, og vi har lagt et sidste rykkergebyr på {{gebyr}} på.\n\n{{opgoerelse}}\n\nINKASSOVARSEL: Betaler du ikke hele beløbet senest {{frist}}, sender vi sagen til inkasso. Det vil medføre yderligere omkostninger for dig (inkassolovens § 10).\n\nBetal her:\n{{link_betal}}' + FOD,
    sms: 'Hej {{kunde_fornavn}}. INKASSOVARSEL: booking {{booking_nr}} ({{i_alt}}) skal betales senest {{frist}}, ellers sendes sagen til inkasso. Betal: {{link_betal}} Mvh {{firma}}' },
];
/** Felter, der kun findes i rykkerbeskeder (ud over de almindelige som {{kunde_fornavn}} og {{link_betal}}). */
export const RYKKER_VARIABLER: [string, string][] = [
  ['opgoerelse', 'Beløbsopgørelse'], ['i_alt', 'I alt nu'], ['hovedstol', 'Fakturaens beløb'], ['gebyr', 'Gebyr på denne rykker'],
  ['renter', 'Renter til i dag'], ['rente_sats', 'Rentesats'], ['forfald', 'Forfaldsdato'], ['frist', 'Ny betalingsfrist'],
];

/** Skabelonen for et niveau: virksomhedens egen udgave, hvis den er rettet, ellers standarden. */
export function skabelonFor(niveau: number, egne: Skabelon[] = []): Skabelon {
  const id = RYKKER_SKABELON_ID[niveau - 1];
  return egne.find((s) => s.id === id) ?? RYKKER_SKABELONER[niveau - 1];
}

/** Beløbsopgørelsen i beskeden (efter denne beskeds gebyr er lagt på). */
export function opgoerelse(a: { booking_nr: number | string; forfald: string; hovedstol: number; gebyrer: number; kompensation: number; renter: number; renteDage: number; sats: number }): string {
  const l = [`Booking ${a.booking_nr} (forfald ${datoTekst(a.forfald, false)}): ${kr(a.hovedstol)}`];
  if (a.gebyrer) l.push(`Rykkergebyrer: ${kr(a.gebyrer)}`);
  if (a.kompensation) l.push(`Kompensation (erhverv): ${kr(a.kompensation)}`);
  if (a.renter) l.push(`Renter (${satsTekst(a.sats)} p.a. i ${a.renteDage} dage): ${kr(a.renter)}`);
  l.push(`I alt: ${kr(a.hovedstol + a.gebyrer + a.kompensation + a.renter)}`);
  return l.join('\n');
}
export const satsTekst = (s: number) => String(s).replace('.', ',') + ' %';

/** Variabler til rykkerbeskeden for niveauet, der sendes nu. */
export function rykkerVariabler(a: {
  i: RykkerInput; r: RykkerRegler; idag: string; send: RykkerSend; booking_nr: number; kunde_navn: string; firma: string; telefon: string; link_betal: string;
}): Record<string, string> {
  const s = a.i.status ?? TOM_STATUS;
  const forfald = plusDage(a.i.dato, a.r.betalingsfrist);
  const gebyrer = s.gebyrer + a.send.gebyr, kompensation = s.kompensation + a.send.kompensation;
  const renteDage = Math.max(0, dageMellem(forfald, a.idag));
  const frist = plusDage(a.idag, a.send.niveau === 4 ? a.r.inkassoFrist : a.r.minDageMellem);
  const fornavn = a.kunde_navn.trim().split(/\s+/)[0] || a.kunde_navn;
  return {
    kunde_navn: a.kunde_navn, kunde_fornavn: fornavn, booking_nr: String(a.booking_nr), firma: a.firma, telefon: a.telefon, link_betal: a.link_betal,
    opgoerelse: opgoerelse({ booking_nr: a.booking_nr, forfald, hovedstol: a.i.hovedstol, gebyrer, kompensation, renter: a.send.rente, renteDage, sats: a.r.rente }),
    i_alt: kr(a.i.hovedstol + gebyrer + kompensation + a.send.rente), hovedstol: kr(a.i.hovedstol), gebyr: kr(a.send.gebyr), renter: kr(a.send.rente),
    rente_sats: satsTekst(a.r.rente), forfald: datoTekst(forfald, false), frist: datoTekst(frist, false),
  };
}

/** Eksempel til forhåndsvisning af rykkerskabeloner under Beskeder. */
export const RYKKER_EKSEMPEL: Record<string, string> = {
  opgoerelse: 'Booking 10482 (forfald 20. oktober): 1.299 kr.\nRykkergebyrer: 100 kr.\nRenter (10,6 % p.a. i 12 dage): 4,53 kr.\nI alt: 1.403,53 kr.',
  i_alt: '1.403,53 kr.', hovedstol: '1.299 kr.', gebyr: '100 kr.', renter: '4,53 kr.', rente_sats: '10,6 %', forfald: '20. oktober', frist: '11. november',
};

// ---------- Referencesatsen: tjekkes 1. januar og 1. juli ----------
// Rykkerrenten er Nationalbankens referencesats (udlånsrenten) + 8 % p.a. Satsen fastsættes 1. januar og 1. juli, så ejer
// og admin får en påmindelse (klokken + push, type 'rente_tjek') de to dage – kun internt, aldrig til kunder. Under
// Rykkere › Regler står, hvornår satsen sidst er bekræftet ("Satsen er tjekket").
export const RENTE_TJEK = {
  titel: 'Tjek rykkerrentens referencesats',
  tekst: 'Tjek Nationalbankens referencesats – rykkerrenten er referencesats + 8 %. Ret den under Rykkere › Regler.',
  link: '/admin/rykkere',
} as const;
export type RenteTjek = { dato: string; af: string } | null;

/** Er det 1. januar eller 1. juli? (Dagene, påmindelsen sendes.) */
export const erRenteTjekDag = (dato: string) => /^\d{4}-(01|07)-01$/.test(dato);
/** Den seneste 1. januar eller 1. juli på eller før datoen. */
export const senesteRenteTjekDag = (dato: string) => `${dato.slice(0, 4)}-${dato.slice(5, 7) >= '07' ? '07' : '01'}-01`;
/** Den næste 1. januar eller 1. juli efter datoen. */
export const naesteRenteTjekDag = (dato: string) => (dato.slice(5, 7) >= '07' ? `${Number(dato.slice(0, 4)) + 1}-01-01` : `${dato.slice(0, 4)}-07-01`);
/** Skal satsen tjekkes? (Ikke bekræftet siden den seneste 1. januar/1. juli.) */
export const renteTjekForfalden = (t: RenteTjek, idag: string) => !t || t.dato < senesteRenteTjekDag(idag);
